// ExamInfoPage.tsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
} from "@mui/material";
import {
  CheckCircle,
  AccessTime,
  TrendingUp,
  Security,
  CalendarMonth,
  AssignmentOutlined,
  PlayArrow,
  Speed,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useAppNavigate } from "@/hooks/useAppNavigate";
import type { QuizBackground } from "@/types/Quiz.types";
import quizApi from "@/services/quizService";

interface UnifiedRowItem {
  id: string;
  sectionId: number;
  title: string;
  duration: string;
  questions: string;
  passingRate: string;
  focus: string;
  tagLabel?: string;
  categoryText?: string;
}

const ExamInfoPage: React.FC = () => {
  const { quizName } = useParams<{ quizName: string }>();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useAppNavigate();
  const { t } = useTranslation("translation", { keyPrefix: "pages.examInfo" });
  const [quizBackground, setQuizBackground] = useState<QuizBackground | undefined>(undefined);

  useEffect(() => {
    if (quizName && quizName.trim().length > 0) {
      quizApi.getQuizByName({ quizName }).then((_quiz) => {
        setQuizBackground(_quiz);
      });
    }
  }, [quizName]);

  const handleLaunch = (parentPaper: string, paperIndex: number, sectionIndex: number) => {
    if (quizBackground) {
      let limit = 5;
      let minutes = 15;
      let sections: number[] = [];
      if (parentPaper && sectionIndex >= 0) {
        limit = parseInt(quizBackground.papers[paperIndex].sections[sectionIndex].questions, 10);
        minutes = parseInt(quizBackground.papers[paperIndex].sections[sectionIndex].duration, 10);
        sections = quizBackground.papers[paperIndex].sections[sectionIndex].sections ?? [];
      }
      navigate(`mock/quiz/${quizBackground.quizName}/mcq`, {
        state: {
          quizId: quizBackground.quizId,
          exam: quizBackground.quizName,
          paper: parentPaper,
          sections: sections,
          limit: limit,
          minutes: minutes,
        },
      });
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh", pb: 10 }}>
      <Container maxWidth="xl">
        {/* Hero Header */}
        <Box sx={{ pt: 8, pb: 6, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Security sx={{ fontSize: 80, color: "primary.main" }} />
          </Box>

          <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: "text.primary" }}>
            {quizBackground?.title}
          </Typography>
          <Typography variant="h5" sx={{ color: "text.secondary", maxWidth: 700, mx: "auto" }}>
            {quizBackground?.description}
          </Typography>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
            {quizBackground?.tags.map((tItem, idx) => (
              <Chip
                key={idx}
                label={tItem.label}
                variant="filled"
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: "primary.main",
                  borderRadius: 1,
                }}
              />
            ))}
          </Box>
        </Box>

        <Grid container spacing={5}>
          {/* Left Column - Main Content */}
          <Grid size={{ xs: 12, md: 8 }}>
            {/* Overview */}
            <Card
              elevation={0}
              sx={{
                mb: 5,
                bgcolor: "background.paper",
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: "text.primary" }}>
                  {t("overview")}
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: "text.secondary" }}>
                  {quizBackground?.overview}
                </Typography>

                <Grid container spacing={3} sx={{ mt: 3 }}>
                  {quizBackground?.specs.map((tItem, idx) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={idx}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                          {tItem.description}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {tItem.title}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Papers List */}
            <Box sx={{ width: "100%", py: 2 }}>
              {/* 1. Master Header Hero Block */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.5px", mb: 1, color: "text.primary" }}>
                  {quizBackground?.title}
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: "850px", mb: 2.5, lineHeight: 1.6 }}>
                  {quizBackground?.overview}
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {quizBackground?.tags.map((tag, idx) => (
                    <Chip
                      key={idx}
                      label={tag.label}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* 2. Key Metrics Specs Matrix */}
              <Grid container spacing={2} sx={{ mb: 5 }}>
                {quizBackground?.specs.map((spec, idx) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={idx}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block" }}>
                        {spec.title}
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, color: "text.primary" }}>
                        {spec.description}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* 3. Adaptive Data Grid Engine */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {quizBackground?.papers.map((paperItem, paperIdx) => {
                  const hasSections = paperItem.sections && paperItem.sections.length > 0;

                  const rowsToRender: UnifiedRowItem[] = hasSections
                    ? (paperItem.sections || []).map((sec, i) => ({
                        id: sec.paper,
                        sectionId: i,
                        title: sec.title,
                        duration: sec.duration,
                        questions: sec.questions,
                        passingRate: sec.passingRate,
                        focus: sec.focus,
                        tagLabel: sec.paper,
                        categoryText: sec.topic,
                      }))
                    : [
                        {
                          id: "all",
                          sectionId: -1,
                          title: t("defaultPaperTitle"),
                          duration: paperItem.duration,
                          questions: paperItem.questions,
                          passingRate: paperItem.passingRate,
                          focus: paperItem.focus,
                          tagLabel: undefined,
                        },
                      ];

                  return (
                    <Card
                      key={paperIdx}
                      elevation={0}
                      sx={{
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                        overflow: "hidden",
                      }}
                    >
                      {/* Module Header Area Container */}
                      <Box
                        sx={{
                          p: 3,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          display: "flex",
                          flexDirection: { xs: "column", lg: "row" },
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", lg: "center" },
                          gap: 2.5,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: hasSections ? 0.5 : 0 }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                px: 1.5,
                                py: 0.25,
                                borderRadius: 1,
                                fontWeight: 700,
                              }}
                            >
                              {paperItem.paper}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 750, color: "text.primary" }}>
                              {paperItem.title}
                            </Typography>
                            {paperItem.level && (
                              <Chip
                                label={paperItem.level}
                                size="small"
                                color={
                                  paperItem.level.includes("Foundation")
                                    ? "success"
                                    : paperItem.level.includes("Core")
                                      ? "primary"
                                      : "warning"
                                }
                                sx={{ fontWeight: 700, borderRadius: 1 }}
                              />
                            )}
                          </Box>

                          {hasSections ? (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                              {t("containsSectionsDesc", { count: paperItem.sections?.length || 0 })}
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}
                            >
                              <Security sx={{ fontSize: "inherit", color: "text.disabled" }} />{" "}
                              <strong>{t("coreAssessmentArea")}</strong> {paperItem.focus}
                            </Typography>
                          )}
                        </Box>

                        {hasSections && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            disableElevation
                            startIcon={<Speed />}
                            endIcon={<PlayArrow />}
                            onClick={() => handleLaunch(paperItem.paper, paperIdx, -1)}
                            sx={{
                              borderRadius: 1,
                              textTransform: "none",
                              fontWeight: 700,
                              px: 3,
                              py: 1,
                              boxShadow: `0px 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                              width: { xs: "100%", lg: "auto" },
                            }}
                          >
                            {t("launchFullPaper")}
                          </Button>
                        )}
                      </Box>

                      {/* Individual Evaluation Row Matrix */}
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {rowsToRender.map((row, rowIdx) => (
                          <Box
                            key={rowIdx}
                            sx={{
                              p: 3,
                              display: "flex",
                              flexDirection: { xs: "column", md: "row" },
                              alignItems: { xs: "flex-start", md: "center" },
                              justifyContent: "space-between",
                              gap: 3,
                              borderBottom: rowIdx !== rowsToRender.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                              transition: "background-color 0.2s",
                              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                            }}
                          >
                            {/* Left Meta Information Area */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                                {row.tagLabel && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      bgcolor: alpha(theme.palette.text.secondary, 0.12),
                                      px: 1,
                                      py: 0.2,
                                      borderRadius: 1,
                                      fontWeight: 700,
                                      color: "text.secondary",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {row.tagLabel}
                                  </Typography>
                                )}
                                <Typography variant="body1" sx={{ fontWeight: hasSections ? 650 : 500, color: "text.primary" }}>
                                  {row.title}
                                </Typography>
                                {row.categoryText && (
                                  <Chip
                                    label={row.categoryText}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: "20px", fontSize: "0.65rem", fontWeight: 700 }}
                                  />
                                )}
                              </Box>
                              {hasSections && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}
                                >
                                  <AssignmentOutlined sx={{ fontSize: "inherit", opacity: 0.7 }} /> {row.focus}
                                </Typography>
                              )}
                            </Box>

                            {/* Middle Statistical Data Points */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <AccessTime fontSize="small" color="action" />
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.disabled", display: "block", lineHeight: 1 }}
                                  >
                                    {t("duration")}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                                    {row.duration} {t("minutes")}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CheckCircle fontSize="small" color="action" />
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.disabled", display: "block", lineHeight: 1 }}
                                  >
                                    {t("metrics")}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                                    {row.questions} MCQs
                                  </Typography>
                                </Box>
                              </Box>
                              {row.passingRate !== "N/A" && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <TrendingUp fontSize="small" color="action" />
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "text.disabled", display: "block", lineHeight: 1 }}
                                    >
                                      {t("passRate")}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 650, color: "success.main" }}>
                                      {row.passingRate}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>

                            {/* Right Execution Buttons */}
                            <Box
                              sx={{ display: "flex", gap: 1.5, width: { xs: "100%", md: "auto" }, justifyContent: "flex-end" }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                disableElevation
                                endIcon={<PlayArrow sx={{ fontSize: "15px" }} />}
                                onClick={() => handleLaunch(paperItem.paper, paperIdx, row.sectionId)}
                                sx={{
                                  borderRadius: 1,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  px: 2,
                                }}
                              >
                                {t("launch")}
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Box>

            <Divider sx={{ my: 6, borderColor: "divider" }} />

            {/* Purpose / Target Audience Section */}
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: "text.primary" }}>
              {quizBackground?.purpose.title}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
              {quizBackground?.purpose.description}
            </Typography>
            <Grid container spacing={2}>
              {quizBackground?.purpose.tags?.map((item, i) => (
                <Grid size={{ xs: 12, sm: 6 }} key={i}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={<Typography sx={{ color: "text.primary" }}>{item}</Typography>} />
                  </ListItem>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Right Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                position: "sticky",
                top: 100,
                bgcolor: "background.paper",
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "text.primary" }}>
                  {t("quickActions")}
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ mb: 2, py: 1.8, borderRadius: 1 }}
                >
                  {t("startFreePractice")}
                </Button>

                <Button
                  disabled
                  variant="outlined"
                  fullWidth
                  size="large"
                  sx={{ mb: 4, borderRadius: 1 }}
                >
                  {t("viewOfficialSchedule")}
                </Button>

                <Divider sx={{ my: 3, borderColor: "divider" }} />

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "text.primary" }}>
                  {t("examInformation")}
                </Typography>

                <List>
                  {quizBackground?.infos.map((info, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CalendarMonth color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontWeight: 600, color: "text.primary" }}>{info.title}</Typography>}
                        secondary={<Typography sx={{ color: "text.secondary" }}>{info.description}</Typography>}
                      />
                    </ListItem>
                  ))}
                </List>

                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: "primary.main" }}>
                    {t("proTip")}
                  </Typography>
                  {quizBackground?.tips.map((tip, idx) => (
                    <Typography key={idx} variant="body2" sx={{ color: "text.secondary", mt: 1, lineHeight: 1.6 }}>
                      {tip}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ExamInfoPage;
